import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Header } from '../Header/Header';
import { Footer } from '../Footer/Footer';
import { ToastProvider } from '../../SharedArea/Toast/ToastProvider';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { signedIn, signedOut } from '../../../Redux/sessionSlice';
import { AuthService } from '../../../Services/AuthService';
import { DIRECTIONS } from '../../../i18n/useTranslation';
import { routes } from '../../../Utils/routes';
import { syncNativeStatusBarTheme } from '../../../Utils/nativeStatusBar';
import './AppLayout.css';

/**
 * The shell. It owns two things beyond layout.
 *
 * One is putting the language, the direction and the theme on <html>, where
 * CSS and the browser's own bidi algorithm can see them. Setting them on a
 * div lower down leaves the page background and the scrollbar on the wrong
 * side.
 *
 * The other is the session. Firebase restores the stored sign-in on its own;
 * subscribing to it here, above every route, is what lets the marketing home
 * send a returning tutor straight into the app instead of asking them to sign
 * in again on every visit. AuthGate reads the same state further down.
 */
export function AppLayout() {
  const dispatch = useAppDispatch();
  const language = useAppSelector(state => state.ui.language);
  const theme = useAppSelector(state => state.ui.theme);
  const sessionStatus = useAppSelector(state => state.session.status);
  const location = useLocation();
  const isUserArea = location.pathname === '/user' || location.pathname.startsWith('/user/');
  // Home and Login are the two entrances to the product and always render
  // dark, regardless of the visitor's system or stored preference; every
  // other screen still follows it. `signed-out` (not `unknown`) is what
  // AuthGate actually shows Login for, so this only forces dark once Login
  // is the thing on screen, not during the brief "checking session" spinner.
  const isEntrance = location.pathname === routes.home || (isUserArea && sessionStatus === 'signed-out');

  useEffect(() => AuthService.onChange(user => {
    if (user) dispatch(signedIn(user));
    else dispatch(signedOut());
  }), [dispatch]);

  useEffect(() => {
    const root = document.documentElement;
    const resolvedTheme = isEntrance ? 'dark' : theme;
    root.lang = language;
    root.dir = DIRECTIONS[language];
    root.dataset['theme'] = resolvedTheme;
    syncNativeStatusBarTheme(resolvedTheme);
  }, [language, theme, isEntrance]);

  // The toast provider wraps both branches: it is how the whole app reports a
  // small action, and a marketing page that could not raise one would be a
  // hole waiting for the first component that tries.
  return (
    <ToastProvider>
      {isUserArea
        ? <Outlet />
        : (
          <div className="app-layout">
            <Header />
            <main className="app-main">
              <Outlet />
            </main>
            <Footer />
          </div>
        )}
    </ToastProvider>
  );
}
